import { ReactNode } from "react";
import { motion, useReducedMotion } from "framer-motion";
import { cn } from "@/lib/utils";
import AmbientHeroBackdrop from "./visuals/AmbientHeroBackdrop";

interface PageHeroProps {
  eyebrow: string;
  title: ReactNode;
  subtitle?: ReactNode;
  variant?: "dark" | "light";
  /** When true, omit the section background so a parent can render imagery behind. */
  transparent?: boolean;
  /** When true (and variant=dark, not transparent), render an animated ambient backdrop. */
  backdrop?: boolean;
  /** When true, center-align eyebrow/title/subtitle/children. */
  centered?: boolean;
  /** Optional visual content placed beside the text on large screens. */
  aside?: ReactNode;
  /**
   * How the copy and the aside line up on wide screens. "center" (default) keeps
   * the existing behaviour; "start" puts both at the same top edge; "fill" also
   * lets the copy column's own children grow to reach the aside's bottom edge.
   */
  asideAlign?: "center" | "start" | "fill";
  /** When true on a light hero, render decorative primary-colour rings. */
  ringsBackdrop?: boolean;
  children?: ReactNode;
  className?: string;
}

const ease = [0.22, 1, 0.36, 1] as const;

/** A short highlight that travels around one decorative hero ring. */
const HeroRingArc = ({ duration, reverse = false, delay = "0s" }: { duration: string; reverse?: boolean; delay?: string }) => (
  <svg
    aria-hidden="true"
    className="campaign-hero-arc absolute inset-0 h-full w-full"
    viewBox="0 0 100 100"
    fill="none"
    style={{ animationDuration: duration, animationDelay: delay, animationDirection: reverse ? "reverse" : "normal" }}
  >
    <circle cx="50" cy="50" r="48" vectorEffect="non-scaling-stroke" />
  </svg>
);

const PageHero = ({ eyebrow, title, subtitle, variant = "dark", transparent = false, backdrop = false, centered = false, aside, asideAlign = "center", ringsBackdrop = false, children, className }: PageHeroProps) => {

  const dark = variant === "dark";
  const reduce = useReducedMotion();
  const y = reduce ? 0 : 14;

  return (
    <section
      className={cn(
        "relative overflow-hidden pt-36 pb-20 md:pt-48 md:pb-28",
        transparent
          ? dark ? "text-seabed-foreground" : "text-foreground"
          : dark ? "bg-seabed text-seabed-foreground" : "bg-background text-foreground",
        className,
      )}
    >
      {!dark && ringsBackdrop && (
        <div className="pointer-events-none absolute inset-y-0 right-0 w-full sm:w-4/5 lg:w-3/5" aria-hidden="true" data-testid="page-hero-rings">
          <div
            className="absolute inset-0"
            style={{ background: "radial-gradient(circle at 75% 50%, hsl(var(--primary) / 0.10), transparent 58%)" }}
          />
          <div className="absolute left-[25%] top-1/2 aspect-square w-[120%] -translate-y-1/2 rounded-full border border-primary/10"><HeroRingArc duration="26s" delay="-8s" /></div>
          <div className="absolute left-[47%] top-1/2 aspect-square w-[82%] -translate-y-1/2 rounded-full border border-primary/15"><HeroRingArc duration="18s" reverse delay="-4s" /></div>
          <div className="absolute left-[68%] top-1/2 aspect-square w-[44%] -translate-y-1/2 rounded-full border border-primary/20"><HeroRingArc duration="12s" /></div>
        </div>
      )}
      {dark && !transparent && (
        <div
          className="absolute inset-0 opacity-50"
          aria-hidden
          style={{
            background:
              "radial-gradient(ellipse 60% 50% at 50% 100%, hsl(var(--primary) / 0.14), transparent 70%)",
          }}
        />
      )}
      {dark && backdrop && !transparent && <AmbientHeroBackdrop />}
      <div className="container relative z-10">
        <div className={cn(aside && cn("lg:grid lg:grid-cols-12 lg:gap-12", asideAlign === "start" ? "lg:items-start" : asideAlign === "fill" ? "lg:items-stretch" : "lg:items-center"))}>
        <div className={cn("max-w-4xl", centered && "mx-auto text-center", aside && "lg:col-span-7", asideAlign === "fill" && "lg:flex lg:flex-col")}>
          <motion.p
            initial={{ opacity: 0, y }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, ease, delay: 0.05 }}
            className={cn(
              "eyebrow",
              dark ? "text-seabed-foreground/60" : "text-muted-foreground",
            )}
          >
            {eyebrow}
          </motion.p>
          <motion.h1
            initial={{ opacity: 0, y: y + 4 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1, ease, delay: 0.18 }}
            className={cn(
              "display mt-6 text-hero",
              dark ? "text-seabed-foreground" : "text-foreground",
            )}
          >
            {title}
          </motion.h1>
          {subtitle && (
            <motion.p
              initial={{ opacity: 0, y }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.9, ease, delay: 0.32 }}
              className={cn(
                "mt-8 max-w-2xl text-lg md:text-xl leading-relaxed",
                dark ? "text-seabed-foreground/75" : "text-foreground/70",
                centered && "mx-auto",
              )}
            >
              {subtitle}
            </motion.p>
          )}
          {children && (
            <motion.div
              initial={{ opacity: 0, y }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.9, ease, delay: 0.46 }}
              className={cn("mt-12", asideAlign === "fill" && "lg:flex lg:flex-1 lg:flex-col")}
            >
              {children}
            </motion.div>
          )}
        </div>
        {aside && <div className="mt-12 lg:col-span-5 lg:mt-0" data-testid="page-hero-aside">{aside}</div>}
        </div>
      </div>

    </section>
  );
};

export default PageHero;
